import React, { useEffect, useMemo, useRef, useState } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { C, font, mono, btnP, btnS } from "../../../lib/styles.js";
import {
  BLANK_JOB,
  equipmentNamesFromIds,
  FMT_DATE_FULL,
  FMT_DATE_SHORT,
  fmt,
  fmtDate,
  getJobDateRange,
  isoDateOrEmpty,
  quoteCaptureDate,
  sortSessions,
} from "../../../lib/utils.js";
import { SESSION_TYPE_LABELS, STATUS_COLORS_MAP } from "../../../lib/constants.js";
import { resolveQuoteRevenue } from "../../../lib/builderHelpers.js";
import { scheduleConflicts, conflictsForQuote, conflictSentences, isHoldingQuote, isStaleQuote, quoteEquipmentIds, describeHoldDates, quoteHoldDates } from "../../../lib/holds.js";
import QuotePdfPreview from "./QuotePdfPreview.jsx";
import QuoteVersionHistoryModal from "./QuoteVersionHistoryModal.jsx";
import notify from "../../../lib/notify.jsx";

const QSC = STATUS_COLORS_MAP(C);

/* The pipeline model (approved mockup quotes-pipeline-v1, 2026-06-10).
   Every quote lands in exactly one bucket:
   - trash:   deletedAt set (trashedQuotes), any prior status
   - drafts:  draft flag, not deleted
   - booked:  live linked job — it IS a job; never listed in a tab, but search
              still surfaces it as a dimmed row that opens the job
   - closed:  lost / archived / completed-without-live-job ("Won")
   - open:    quoted, plus booked-without-live-job ("zombie" — flagged, and
              excluded from the pipeline number)                              */
const TAB_DEFS = [
  { key: "open", label: "Open" },
  { key: "drafts", label: "Drafts" },
  { key: "closed", label: "Closed" },
  { key: "trash", label: "Trash" },
];

const STATE_LABEL = {
  quoted: "Quoted",
  booked_nojob: "Booked · no job",
  booked: "Booked",
  completed: "Won",
  draft: "Draft",
  lost: "Lost",
  archived: "Archived",
  trashed: "In Trash",
};

function stateColor(state) {
  if (state === "booked_nojob") return C.amber;
  if (state === "draft") return C.amber;
  if (state === "trashed") return C.muted;
  return QSC[state] || QSC.quoted;
}

// Sort options apply to OPEN only — capture-date order is meaningless for
// closed/trashed quotes (they sort by when they closed/were deleted instead).
// Mechanical wording (ascending/descending) on purpose: "Soonest/Furthest"
// read backwards against real data, where past and future capture dates mix
// and there is no single "soonest" direction (Ivin, live test 2026-06-10).
const SORT_OPTIONS = [
  { key: "date-asc", label: "Date (ascending)", grouped: true, field: "_date", emptyLabel: "No capture date" },
  { key: "date-desc", label: "Date (descending)", grouped: true, field: "_date", emptyLabel: "No capture date" },
  { key: "created-asc", label: "Date created (ascending)", grouped: true, field: "_created", emptyLabel: "No date" },
  { key: "created-desc", label: "Date created (descending)", grouped: true, field: "_created", emptyLabel: "No date" },
  { key: "client", label: "Client A–Z", grouped: false },
  { key: "total-hi", label: "Total high → low", grouped: false },
  { key: "total-lo", label: "Total low → high", grouped: false },
];
const sortOption = (sortKey) => SORT_OPTIONS.find(o => o.key === sortKey);

const monthKey = (iso) => (iso ? iso.slice(0, 7) : "none");
const monthLabel = (iso, emptyLabel) => {
  if (!iso) return emptyLabel;
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

// Compact capture-date label for a row: single day → "Jun 18"; multi-day in one
// month → "Jun 26–28"; spanning months → "Jun 28 – Jul 2"; none → "TBD".
function rowDateLabel(sessions, captureDate) {
  const { start, end } = getJobDateRange({ sessions, captureDate });
  if (!start) return "TBD";
  if (start === end) return fmtDate(start, FMT_DATE_SHORT);
  const startShort = fmtDate(start, FMT_DATE_SHORT);
  if (start.slice(0, 7) === end.slice(0, 7)) {
    const endDay = Number(end.split("-")[2]);
    return `${startShort}–${endDay}`;
  }
  return `${startShort} – ${fmtDate(end, FMT_DATE_SHORT)}`;
}

// LOCAL calendar day — toISOString() is UTC, which flips to "tomorrow" during a
// US evening, marking today's follow-ups overdue and letting restore-to-open
// clear a same-day follow-up as stale (codex F4).
const isoDay = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const dayDiff = (iso, todayIso) =>
  Math.round((new Date(`${iso}T00:00:00`) - new Date(`${todayIso}T00:00:00`)) / 86400000);

// Phone detection per app convention: matchMedia, never window.innerWidth.
function useIsPhone() {
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isPhone;
}

export default function SavedQuotes({ onOpenJob, onCreateQuote, onEditQuote, onDuplicateQuote }) {
  const {
    savedQuotes = [],
    draftQuotes = [],
    trashedQuotes = [],
    jobs = [],
    contacts = [],
    clients = [],
    crewRoster = [],
    equipmentMap,
    quotesLoading,
    quotesError,
    bookJob,
    updateQuoteStatus,
    updateQuoteField,
    deleteQuote,
    restoreQuote,
    permanentDeleteQuote,
    deleteDraft,
    markFollowedUp,
    snoozeBizDays,
    snoozeToDate,
    // The lock and the unsaved-edits check (lib/quotePersistence.js). Absent in older fixtures.
    quoteBusy = null,
    unsavedEditsNotice,
  } = useData();
  // While a quote is being saved, booked or restored its row accepts no action; the
  // panel says what is happening instead.
  // The quote being saved, booked or opened, and the one it is replacing while opening.
  const lockedLabel = (q) => (quoteBusy && q?.id && (quoteBusy.quoteId === q.id || quoteBusy.targetId === q.id) ? quoteBusy.label : "");

  const isPhone = useIsPhone();
  const [tab, setTab] = useState("open");
  const [chip, setChip] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date-asc");
  const [selectedId, setSelectedId] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [eraseArmId, setEraseArmId] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null); // "trash" | "erase" | "drafts"
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pdfQuote, setPdfQuote] = useState(null);
  const [versionsQuote, setVersionsQuote] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [error, setError] = useState("");

  const todayIso = isoDay(new Date());

  const decorated = useMemo(() => {
    // Holds (2026-09-04): every holding quote's rows against every live job, once
    // per list, so each row can read its own collisions (approved mockup Frame D).
    // equipmentMap comes from context and changes with the studio equipment settings.
    const schedule = scheduleConflicts(savedQuotes, jobs, crewRoster, { equipmentMap });
    const now = new Date();
    const decorate = (q, flags = {}) => {
      // useData().jobs is activeJobs-filtered, so a deleted linked job reads as
      // "no live job" — which is exactly what the zombie state needs.
      const linkedJob = jobs.find(j => j.quoteId === q.id) || null;
      const status = String(q.jobStatus || "quoted").toLowerCase();
      let bucket, state;
      if (flags.trashed) { bucket = "trash"; state = "trashed"; }
      else if (flags.draft) { bucket = "drafts"; state = "draft"; }
      else if (linkedJob) { bucket = "booked"; state = "booked"; }
      else if (status === "lost" || status === "archived" || status === "completed") { bucket = "closed"; state = status; }
      else if (status === "booked") { bucket = "open"; state = "booked_nojob"; }
      else { bucket = "open"; state = "quoted"; }

      const sessions = sortSessions(q.sessions || []);
      // isoDateOrEmpty: legacy quotes carry junk like scanDate:"TBD" — unsanitized it
      // sorts above every real date and renders an "Invalid Date" month header.
      // _date is the CAPTURE date only — no quoteDate/savedAt fallback. A row
      // that reads "TBD" must also sort/group as undated (bottom, under "No
      // capture date"), not secretly by its creation date; creation ordering
      // has its own explicit sorts via _created.
      // A single date marked TBD is no date here either (quoteCaptureDate), so a row
      // reading "TBD" isn't grouped under the month of its hidden date.
      const date = isoDateOrEmpty(quoteCaptureDate(q));
      const holding = !flags.trashed && !flags.draft && isHoldingQuote(q, { hasLiveJob: !!linkedJob });
      const conflictRows = holding ? conflictsForQuote(q.id, schedule) : [];

      // Follow-up tone only matters while the quote is genuinely open.
      let fu = null;
      if (state === "quoted" && q.followUpDate) {
        const n = dayDiff(q.followUpDate, todayIso);
        if (n < 0) fu = { tone: "overdue", label: `Follow-up ${Math.abs(n)}d overdue`, due: `was due ${fmtDate(q.followUpDate, FMT_DATE_SHORT)} — ${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} overdue` };
        else if (n === 0) fu = { tone: "today", label: "Follow-up due today", due: "due today" };
        else if (n <= 7) fu = { tone: "soon", label: `Follow-up in ${n}d`, due: `scheduled ${fmtDate(q.followUpDate, FMT_DATE_SHORT)}` };
        else fu = { tone: "later", label: "", due: `scheduled ${fmtDate(q.followUpDate, FMT_DATE_SHORT)}` };
      }

      // Right-column meta per bucket: capture date for open work; the lifecycle
      // date everywhere else.
      const updatedDay = isoDateOrEmpty(String(q.updatedAt || q.savedAt || "").slice(0, 10)) || isoDateOrEmpty(q.quoteDate) || "";
      const deletedDay = isoDateOrEmpty(String(q.deletedAt || "").slice(0, 10)) || "";
      const metaLabel =
        bucket === "trash" ? (deletedDay ? `Deleted ${fmtDate(deletedDay, FMT_DATE_SHORT)}` : "Deleted") :
        bucket === "drafts" ? (updatedDay ? `Edited ${fmtDate(updatedDay, FMT_DATE_SHORT)}` : "Draft") :
        bucket === "closed" ? (updatedDay ? fmtDate(updatedDay, FMT_DATE_SHORT) : "") :
        rowDateLabel(sessions, date);

      return {
        ...q,
        _bucket: bucket,
        _state: state,
        _date: date,
        // Creation date for the "Date created" sorts — quoteDate, else savedAt.
        _created: isoDateOrEmpty(q.quoteDate) || isoDateOrEmpty(String(q.savedAt || "").slice(0, 10)) || "",
        _metaLabel: metaLabel,
        _sortStamp: String(q.updatedAt || q.savedAt || q.quoteDate || ""),
        _deletedStamp: String(q.deletedAt || ""),
        _linkedJob: linkedJob,
        // Display names from the canonical ids (the live map), else the legacy reader.
        _equipment: equipmentNamesFromIds(quoteEquipmentIds(q, equipmentMap), equipmentMap),
        _sessions: sessions,
        _fu: fu,
        // Holds: does this quote hold its dates right now, which dates collide,
        // the admin sentences for the panel, and the 30-day stale flag.
        _holding: holding,
        _holdDates: holding ? quoteHoldDates(q).map(r => r.date) : [],
        _conflicts: conflictRows.map(r => r.date),
        _conflictLines: conflictSentences(conflictRows, { equipmentMap }),
        _stale: holding && isStaleQuote(q, now),
      };
    };
    return [
      ...draftQuotes.map(q => decorate(q, { draft: true })),
      ...savedQuotes.map(q => decorate(q, {})),
      ...trashedQuotes.map(q => decorate(q, { trashed: true })),
    ];
  }, [savedQuotes, draftQuotes, trashedQuotes, jobs, crewRoster, equipmentMap, todayIso]);

  const counts = useMemo(() => ({
    open: decorated.filter(q => q._bucket === "open").length,
    drafts: decorated.filter(q => q._bucket === "drafts").length,
    closed: decorated.filter(q => q._bucket === "closed").length,
    trash: decorated.filter(q => q._bucket === "trash").length,
    fu: decorated.filter(q => q._bucket === "open" && q._fu && (q._fu.tone === "overdue" || q._fu.tone === "today")).length,
    lost: decorated.filter(q => q._state === "lost").length,
    archived: decorated.filter(q => q._state === "archived").length,
    won: decorated.filter(q => q._state === "completed").length,
  }), [decorated]);

  // Pipeline = open, genuinely quoted work only. Zombies (booked, job deleted)
  // are won revenue with a broken link, not open pipeline — excluded.
  // resolveQuoteRevenue recomputes from line items (JM14): a quote whose cached
  // total is missing or stale is real pipeline money, not $0.
  const pipelineTotal = useMemo(() => decorated.reduce((sum, q) =>
    (q._bucket === "open" && q._state === "quoted") ? sum + resolveQuoteRevenue(q) : sum
  , 0), [decorated]);

  const clientsList = useMemo(() =>
    Array.from(new Set(decorated.filter(q => q._bucket !== "booked").map(q => q.clientName).filter(Boolean))).sort()
  , [decorated]);

  const qText = search.trim().toLowerCase();
  const matchesSearch = (q) => !qText || [
    q.clientName, q.contactName, q.summary, q.jobLocation, q.locationVenue, q.id,
  ].some(v => String(v || "").toLowerCase().includes(qText));

  const rows = useMemo(() => {
    const filtered = decorated.filter(q => {
      if (q._bucket !== tab) return false;
      if (tab === "open" && chip === "fu" && !(q._fu && (q._fu.tone === "overdue" || q._fu.tone === "today"))) return false;
      if (tab === "closed" && chip !== "all" && q._state !== chip) return false;
      if (clientFilter !== "all" && q.clientName !== clientFilter) return false;
      return matchesSearch(q);
    });
    // Undated rows always sink to the bottom regardless of direction.
    const byField = (field, dir) => (a, b) => {
      const ad = a[field] || "", bd = b[field] || "";
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return dir === "asc" ? ad.localeCompare(bd) : bd.localeCompare(ad);
    };
    if (tab === "trash") return filtered.sort((a, b) => b._deletedStamp.localeCompare(a._deletedStamp));
    if (tab === "closed") return filtered.sort((a, b) => b._sortStamp.localeCompare(a._sortStamp));
    if (tab === "drafts") return filtered.sort((a, b) => b._sortStamp.localeCompare(a._sortStamp));
    const comparators = {
      "date-asc": byField("_date", "asc"),
      "date-desc": byField("_date", "desc"),
      "created-asc": byField("_created", "asc"),
      "created-desc": byField("_created", "desc"),
      client: (a, b) => (a.clientName || "Untitled").localeCompare(b.clientName || "Untitled") || byField("_date", "asc")(a, b),
      "total-hi": (a, b) => (Number(b.total) || 0) - (Number(a.total) || 0),
      "total-lo": (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
    };
    return filtered.sort(comparators[sortBy] || comparators["date-asc"]);
  }, [decorated, tab, chip, clientFilter, qText, sortBy]);

  // Booked quotes are jobs now — never in a tab, but search still finds them so
  // "where's that quote I sent in March?" always has an answer. They obey the
  // client filter like every other row (codex F5).
  const bookedMatches = useMemo(() => (
    qText
      ? decorated.filter(q =>
          q._bucket === "booked"
          && (clientFilter === "all" || q.clientName === clientFilter)
          && matchesSearch(q))
      : []
  ), [decorated, qText, clientFilter]);

  // Month-grouped for Open date/created sorts (headers follow whichever date
  // field is sorting); one flat group everywhere else.
  const groups = useMemo(() => {
    const opt = sortOption(sortBy);
    if (tab !== "open" || !opt?.grouped) return [{ key: "all", label: null, rows }];
    const out = [];
    const byKey = new Map();
    rows.forEach(q => {
      const iso = q[opt.field];
      const k = monthKey(iso);
      if (!byKey.has(k)) { const g = { key: k, label: monthLabel(iso, opt.emptyLabel), rows: [] }; byKey.set(k, g); out.push(g); }
      byKey.get(k).rows.push(q);
    });
    return out;
  }, [rows, sortBy, tab]);

  // First visit auto-previews the top row. After an EXPLICIT selection, a quote
  // that's been filtered/sorted out clears to the empty state — no silent jump
  // to row 1. selectedId === null means "never picked anything yet".
  const selected = selectedId === null
    ? (rows[0] || null)
    : (rows.find(q => q.id === selectedId) || null);
  const previewJob = useMemo(() => (
    (selected && (selected._state === "quoted" || selected._state === "booked_nojob"))
      // Preview what booking would actually record: resolveQuoteRevenue, not the
      // raw cached total BLANK_JOB seeds — keeps this consistent with the
      // pipeline sum and with bookJob itself (JM14/JM15).
      ? { ...BLANK_JOB(selected, contacts, clients), grossRevenue: resolveQuoteRevenue(selected) }
      : null
  ), [selected, contacts, clients]);

  // One open row menu at a time; any outside click closes it.
  useEffect(() => {
    if (!menuId) return undefined;
    const close = () => setMenuId(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuId]);

  // Phone sheet locks the page scroll behind it.
  useEffect(() => {
    if (!(isPhone && sheetOpen)) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isPhone, sheetOpen]);

  const switchTab = (key) => {
    setTab(key); setChip("all"); setSelectedId(null);
    setMenuId(null); setEraseArmId(null); setSheetOpen(false);
    setChecked(new Set()); setBulkConfirm(null);
  };

  /* ── Bulk select ──
     `checked` may hold ids that a filter has since hidden; every bulk action
     runs against checkedVisible ONLY, so an invisible row can never be acted
     on. The bar count reflects the same set. */
  const checkedVisible = useMemo(() => rows.filter(r => checked.has(r.id)), [rows, checked]);
  const toggleChecked = (id) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllVisible = () => setChecked(new Set(rows.map(r => r.id)));
  const clearChecked = () => { setChecked(new Set()); setBulkConfirm(null); };

  const runBulk = async (fn, message) => {
    const targets = checkedVisible;
    if (!targets.length) return;
    clearChecked();
    setSelectedId(null);
    setSheetOpen(false);
    // Mutators resolve true/false (false = write failed and rolled back).
    // Settle ALL writes before toasting so the message reflects what actually
    // persisted instead of claiming success up front (codex F1).
    const results = await Promise.all(targets.map(q => Promise.resolve(fn(q))));
    const failed = results.filter(r => r === false).length;
    if (failed) notify.error(`${failed} of ${targets.length} didn't save and were put back — try again.`);
    else notify.info(message(targets.length));
  };
  const plural = (n) => (n === 1 ? "quote" : "quotes");
  const restoreFieldsFor = (q) => ({
    jobStatus: "quoted",
    ...(q.followUpDate && q.followUpDate < todayIso ? { followUpDate: "" } : {}),
  });
  const confirmBulk = () => {
    if (bulkConfirm === "trash") runBulk(q => deleteQuote?.(q.id), n => `Moved ${n} ${plural(n)} to Trash.`);
    else if (bulkConfirm === "erase") runBulk(q => permanentDeleteQuote?.(q.id), n => `Deleted ${n} ${plural(n)} forever.`);
    else if (bulkConfirm === "drafts") runBulk(q => deleteDraft?.(q.id), n => `Deleted ${n} draft${n === 1 ? "" : "s"}.`);
    setBulkConfirm(null);
  };

  // After an action that moves a row out of the current tab, fall back to the
  // auto-top selection instead of pointing at a quote that just left.
  const afterMove = () => {
    setSelectedId(null); setMenuId(null); setEraseArmId(null); setSheetOpen(false);
  };

  // Optimistic UI moves the row immediately; the toast waits for the write
  // outcome so it never claims success for a save that rolled back (codex F1).
  // Only an explicit `false` is failure — fixture mutators resolve undefined.
  const settle = async (op, successMsg) => {
    const ok = await op;
    if (ok === false) notify.error("That didn't save — the quote was put back. Try again.");
    else notify.info(successMsg);
  };

  // A row's actions wait while that quote is being saved, booked or restored.
  const unlessLocked = (fn) => (q, ...rest) => {
    const label = lockedLabel(q);
    if (label) { notify.error(`${label} this quote. Wait for it to finish, then try again.`); return undefined; }
    return fn(q, ...rest);
  };
  const act = {
    select: (q) => {
      setSelectedId(q.id);
      setEraseArmId(null);
      if (isPhone) setSheetOpen(true);
    },
    edit: unlessLocked((q) => onEditQuote?.(q)),
    duplicate: unlessLocked((q) => onDuplicateQuote?.(q)),
    viewPdf: unlessLocked((q) => setPdfQuote(q)),
    versions: (q) => setVersionsQuote(q),
    openJob: (q) => { if (q._linkedJob) onOpenJob?.(q._linkedJob); },
    markLost: unlessLocked((q) => { afterMove(); settle(updateQuoteStatus?.(q.id, "lost"), "Marked lost — moved to Closed."); }),
    archive: unlessLocked((q) => { afterMove(); settle(updateQuoteStatus?.(q.id, "archived"), "Archived — moved to Closed."); }),
    restoreToOpen: unlessLocked((q) => {
      // A quote archived months ago would come back instantly "overdue" — clear
      // the stale date on restore so it rejoins Open quietly.
      const stale = !!(q.followUpDate && q.followUpDate < todayIso);
      afterMove();
      settle(
        updateQuoteField?.(q.id, { jobStatus: "quoted", ...(stale ? { followUpDate: "" } : {}) }),
        stale ? "Restored to Open — stale follow-up cleared." : "Restored to Open.",
      );
    }),
    revertToOpen: unlessLocked((q) => { afterMove(); settle(updateQuoteField?.(q.id, { jobStatus: "quoted" }), "Reverted to Open."); }),
    trash: unlessLocked((q) => { afterMove(); settle(deleteQuote?.(q.id), "Moved to Trash — restore it any time."); }),
    restoreFromTrash: unlessLocked((q) => {
      const s = String(q.jobStatus || "quoted").toLowerCase();
      const dest = (s === "lost" || s === "archived" || s === "completed") ? "Closed" : "Open";
      afterMove();
      settle(restoreQuote?.(q.id), `Restored to ${dest}.`);
    }),
    eraseAsk: (q) => {
      setSelectedId(q.id);
      setEraseArmId(q.id);
      if (isPhone) setSheetOpen(true);
    },
    eraseCancel: () => setEraseArmId(null),
    eraseConfirm: unlessLocked((q) => {
      afterMove();
      if (q._state === "draft") settle(deleteDraft?.(q.id), "Draft deleted.");
      else settle(permanentDeleteQuote?.(q.id), "Deleted forever.");
    }),
    followedUp: unlessLocked((q) => settle(markFollowedUp?.(q.id), "Follow-up cleared.")),
    snooze: unlessLocked((q, n) => snoozeBizDays?.(q.id, n)),
    snoozeTo: unlessLocked((q, date) => snoozeToDate?.(q.id, date)),
    book: unlessLocked(async (q) => {
      if (!q || bookingId) return;
      setError("");
      setBookingId(q.id);
      try {
        await bookJob?.(q, job => onOpenJob?.(job));
      } catch (e) {
        console.error("Quote booking failed:", e);
        setError(e?.userMessage || "Could not book this quote. Please try again.");
      } finally {
        setBookingId(null);
      }
    }),
  };

  // Loading or failed: the list, tabs and command strip are all unavailable.
  const listBlocked = (quotesLoading && savedQuotes.length === 0) || !!quotesError;

  const panelProps = selected ? {
    quote: selected,
    previewJob,
    booking: bookingId === selected.id,
    eraseArmed: eraseArmId === selected.id,
    locked: lockedLabel(selected),
    // The lock's own message once it passed its time limit (what the builder's overlay shows).
    lockMessage: lockedLabel(selected) && quoteBusy?.stuck ? quoteBusy.message : "",
    unsavedNote: unsavedEditsNotice?.(selected.id) || "",
    act,
  } : null;

  return (
    <div style={{ fontFamily: font }}>
      {/* Checkbox reveal is hover-driven (impossible with inline styles); phones
          have no hover, so the box stays visible there. Same injected-<style>
          pattern as QuoteBuilder's media queries. */}
      <style>{`
        .sq-cb { opacity: 0.35; }
        .sq-row:hover .sq-cb, .sq-cb-on { opacity: 1; }
        /* !important: these buttons carry inline background styles, which plain
           class rules cannot override. */
        .sq-seg > button:hover, .sq-kebab:hover, .sq-menu > button:hover { background: ${C.surfaceAlt} !important; }
        /* Action dock (approved mockup quotes-actions-v1 variant B, 2026-08-27).
           Sticky to the bottom of whichever scroll container holds the panel, so
           the actions never scroll away and never slide under the topbar. The
           negative margins cancel the container's padding so it reads as a real
           footer edge; the offsets differ because the panel pads 18 and the
           sheet pads 16/28. */
        .sq-dock {
          position: sticky;
          bottom: -18px;
          margin: 16px -18px -18px;
          padding: 12px 18px;
          background: ${C.surface};
          border-top: 1px solid ${C.border};
          border-radius: 0 0 10px 10px;
          z-index: 5;
        }
        .sq-sheet .sq-dock {
          bottom: -28px;
          margin: 16px -16px -28px;
          padding: 12px 16px;
          border-radius: 0;
        }
        @media (max-width: 767px) {
          .sq-cb { opacity: 0.85; }
          .sq-cb-on { opacity: 1; }
        }
      `}</style>
      <div style={S.pageHeader}>
        <div>
          <div className="sq-title" style={S.pageTitle}>Quotes</div>
          <div style={S.pageSub}>Your quote pipeline. A booked quote becomes a job — find it in <strong style={{ fontWeight: 500, color: C.text }}>Jobs</strong>.</div>
        </div>
        {/* New Quote normally lives in the command strip below, which only
            renders once the list loads. If loading stalls or fails, creating a
            quote must still be possible — so the button falls back to here.
            The two are mutually exclusive; it never renders twice. */}
        {listBlocked && (
          <button type="button" style={btnP} onClick={onCreateQuote}>New Quote</button>
        )}
      </div>

      {quotesLoading && savedQuotes.length === 0 ? (
        <div style={S.empty}>Loading quotes...</div>
      ) : quotesError ? (
        <div style={{ ...S.empty, color: C.danger }}>Failed to load quotes: {quotesError}</div>
      ) : (
        <>
          {/* Command strip: create first, then the view switcher, with the
              pipeline number parked at the far end. New Quote leads the strip so
              it sits directly above the list column it feeds, instead of alone
              in the page corner (approved mockup quotes-actions-v1 variant B). */}
          <div className="sq-controls" style={S.controlRow}>
            <button type="button" style={btnP} onClick={onCreateQuote}>New Quote</button>
            <div className="sq-tabs" style={S.viewToggle}>
              {TAB_DEFS.map(t => {
                const active = tab === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => switchTab(t.key)} style={{
                    padding: "6px 16px",
                    background: active ? C.text : "transparent",
                    color: active ? "#FFF" : C.muted,
                    border: "none",
                    fontFamily: font,
                    fontSize: 13,
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: 7,
                    whiteSpace: "nowrap",
                  }}>
                    {t.label}
                    <span style={{
                      fontSize: 11,
                      fontVariantNumeric: "tabular-nums",
                      color: active ? "rgba(255,255,255,0.7)" : C.muted,
                    }}>{counts[t.key]}</span>
                  </button>
                );
              })}
            </div>
            {/* marginLeft:auto rather than a flex spacer — a spacer element
                takes its own zero-height line once the strip wraps on a phone. */}
            <div
              className="sq-pipeline"
              style={{ ...S.pipeline, marginLeft: "auto" }}
              title="Live, open quotes only — drafts, booked and closed excluded."
              // title alone is invisible to touch and inconsistently announced.
              aria-label={`Open pipeline ${fmt(pipelineTotal)}. Live, open quotes only — drafts, booked and closed excluded.`}
            >
              <span style={S.pipelineLabel}>Open Pipeline</span>
              <span style={S.pipelineValue}>{fmt(pipelineTotal)}</span>
            </div>
          </div>

          {/* Filters and state chips share one band — four stacked control rows
              pushed the first quote below the fold. */}
          <div className="sq-filters" style={S.filterRow}>
            <select style={S.select} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
              <option value="all">All clients</option>
              {clientsList.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
            {tab === "open" && (
              <select style={S.select} value={sortBy} onChange={e => setSortBy(e.target.value)} title="Sort the list" aria-label="Sort quotes">
                {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>Sort: {o.label}</option>)}
              </select>
            )}
            <SearchInput value={search} onChange={setSearch} />
            {tab === "open" && (
              <div className="sq-chips" style={S.chipsRow}>
                <Chip active={chip === "all"} onClick={() => setChip("all")} label="All" n={counts.open} />
                <Chip active={chip === "fu"} onClick={() => setChip("fu")} label="Needs follow-up" n={counts.fu} />
              </div>
            )}
            {tab === "closed" && (
              <div className="sq-chips" style={S.chipsRow}>
                <Chip active={chip === "all"} onClick={() => setChip("all")} label="All" n={counts.closed} />
                <Chip active={chip === "lost"} onClick={() => setChip("lost")} label="Lost" n={counts.lost} />
                <Chip active={chip === "archived"} onClick={() => setChip("archived")} label="Archived" n={counts.archived} />
                <Chip active={chip === "completed"} onClick={() => setChip("completed")} label="Won" n={counts.won} />
              </div>
            )}
            {(clientFilter !== "all" || search.trim()) && (
              <button onClick={() => { setClientFilter("all"); setSearch(""); }} style={S.clearBtn}>
                Clear filters
              </button>
            )}
          </div>

          {checkedVisible.length > 0 && (
            <div className="sq-bulkbar" style={S.bulkBar}>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{checkedVisible.length} selected</span>
              {bulkConfirm ? (
                <span style={S.bulkConfirmWrap}>
                  <span>
                    {bulkConfirm === "trash" && `Move ${checkedVisible.length} to Trash?`}
                    {bulkConfirm === "erase" && `Delete ${checkedVisible.length} forever? This cannot be undone.`}
                    {bulkConfirm === "drafts" && `Delete ${checkedVisible.length} draft${checkedVisible.length === 1 ? "" : "s"} permanently?`}
                  </span>
                  <button type="button" style={S.bulkLink} onClick={() => setBulkConfirm(null)}>Cancel</button>
                  <button type="button" style={{ ...S.bulkBtn, ...S.bulkBtnDanger }} onClick={confirmBulk}>Confirm</button>
                </span>
              ) : (
                <>
                  {tab === "open" && (
                    <>
                      <button type="button" style={S.bulkBtn} onClick={() => runBulk(q => updateQuoteStatus?.(q.id, "archived"), n => `Archived ${n} ${plural(n)} — moved to Closed.`)}>Archive</button>
                      <button type="button" style={{ ...S.bulkBtn, ...S.bulkBtnDanger }} onClick={() => setBulkConfirm("trash")}>Move to Trash</button>
                    </>
                  )}
                  {tab === "drafts" && (
                    <button type="button" style={{ ...S.bulkBtn, ...S.bulkBtnDanger }} onClick={() => setBulkConfirm("drafts")}>Delete drafts</button>
                  )}
                  {tab === "closed" && (
                    <>
                      <button type="button" style={S.bulkBtn} onClick={() => runBulk(q => updateQuoteField?.(q.id, restoreFieldsFor(q)), n => `Restored ${n} ${plural(n)} to Open.`)}>Restore to Open</button>
                      <button type="button" style={{ ...S.bulkBtn, ...S.bulkBtnDanger }} onClick={() => setBulkConfirm("trash")}>Move to Trash</button>
                    </>
                  )}
                  {tab === "trash" && (
                    <>
                      <button type="button" style={S.bulkBtn} onClick={() => runBulk(q => restoreQuote?.(q.id), n => `Restored ${n} ${plural(n)}.`)}>Restore</button>
                      <button type="button" style={{ ...S.bulkBtn, ...S.bulkBtnDanger }} onClick={() => setBulkConfirm("erase")}>Delete forever</button>
                    </>
                  )}
                  {checkedVisible.length < rows.length && (
                    <button type="button" style={S.bulkLink} onClick={selectAllVisible}>Select all {rows.length} in view</button>
                  )}
                  <button type="button" style={S.bulkLink} onClick={clearChecked}>Clear</button>
                </>
              )}
            </div>
          )}

          {error && <div style={S.error}>{error}</div>}

          <div className="sq-layout" style={S.layout}>
            <QuotesList
              tab={tab}
              groups={groups}
              totalRows={rows.length}
              bookedMatches={bookedMatches}
              filtersActive={clientFilter !== "all" || !!qText || chip !== "all"}
              selectedId={selected?.id}
              menuId={menuId}
              onToggleMenu={(id) => setMenuId(prev => prev === id ? null : id)}
              onCloseMenu={() => setMenuId(null)}
              checkedIds={checked}
              onToggleChecked={toggleChecked}
              act={act}
            />
            <div className="sq-preview" style={S.preview}>
              {panelProps
                ? <PanelBody key={selected.id} {...panelProps} />
                : <div style={S.previewEmpty}>Select a quote to see details and actions.</div>}
            </div>
          </div>

          {isPhone && sheetOpen && panelProps && (
            <>
              <div className="sq-sheet-backdrop" onClick={() => setSheetOpen(false)} />
              <div className="sq-sheet" role="dialog" aria-label="Quote details">
                <div className="sq-sheet-grab" />
                <button type="button" className="sq-sheet-close" onClick={() => setSheetOpen(false)}>✕</button>
                <PanelBody key={`sheet-${selected.id}`} {...panelProps} />
              </div>
            </>
          )}

          {pdfQuote && (
            <QuotePdfPreview
              // The saved record as it is now: a save can land while the PDF is open.
              quote={[...savedQuotes, ...draftQuotes].find((x) => x.id === pdfQuote.id) || pdfQuote}
              notice={unsavedEditsNotice?.(pdfQuote.id) ? `${unsavedEditsNotice(pdfQuote.id)} This PDF shows the saved quote without them. Preview in the builder shows them.` : ""}
              onClose={() => setPdfQuote(null)}
              closeLabel="✕ Close"
              lock={lockedLabel(pdfQuote) ? quoteBusy : null}
            />
          )}
          {versionsQuote && (
            <QuoteVersionHistoryModal
              quote={versionsQuote}
              linkedJob={versionsQuote._linkedJob || null}
              onClose={() => setVersionsQuote(null)}
              // Cancel in the Unsaved dialog goes back to the quote: Edit on the quote
              // already open with edits shows the builder as it is.
              onOpenBuilder={() => act.edit(versionsQuote)}
            />
          )}
        </>
      )}
    </div>
  );
}

function Chip({ active, onClick, label, n }) {
  return (
    <button type="button" onClick={onClick} style={{ ...S.chipF, ...(active ? S.chipFActive : null) }}>
      {label}
      <span style={{ opacity: 0.65, marginLeft: 5, fontVariantNumeric: "tabular-nums" }}>{n}</span>
    </button>
  );
}

function SearchInput({ value, onChange }) {
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
      <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: C.muted, pointerEvents: "none", fontSize: 12 }}>⌕</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Search client, contact, summary…"
        style={S.search}
      />
    </div>
  );
}

function menuItemsFor(q, act) {
  const s = q._state;
  if (s === "quoted") return [
    { label: "Edit", onClick: () => act.edit(q) },
    { label: "Duplicate", onClick: () => act.duplicate(q) },
    { label: "View PDF", onClick: () => act.viewPdf(q) },
    null,
    { label: "Mark Lost", onClick: () => act.markLost(q) },
    { label: "Archive", onClick: () => act.archive(q) },
    null,
    { label: "Move to Trash", danger: true, onClick: () => act.trash(q) },
  ];
  if (s === "booked_nojob") return [
    { label: "Edit", onClick: () => act.edit(q) },
    { label: "View PDF", onClick: () => act.viewPdf(q) },
    null,
    { label: "Revert to Open", onClick: () => act.revertToOpen(q) },
    { label: "Move to Trash", danger: true, onClick: () => act.trash(q) },
  ];
  if (s === "draft") return [
    { label: "Continue editing", onClick: () => act.edit(q) },
    null,
    { label: "Delete draft", danger: true, onClick: () => act.eraseAsk(q) },
  ];
  if (s === "trashed") return [
    { label: "Restore", onClick: () => act.restoreFromTrash(q) },
    null,
    { label: "Delete forever", danger: true, onClick: () => act.eraseAsk(q) },
  ];
  if (s === "completed") return [
    { label: "Edit", onClick: () => act.edit(q) },
    { label: "Duplicate", onClick: () => act.duplicate(q) },
    { label: "View PDF", onClick: () => act.viewPdf(q) },
    null,
    { label: "Move to Trash", danger: true, onClick: () => act.trash(q) },
  ];
  // lost / archived
  return [
    { label: "Restore to Open", onClick: () => act.restoreToOpen(q) },
    { label: "Edit", onClick: () => act.edit(q) },
    { label: "Duplicate", onClick: () => act.duplicate(q) },
    { label: "View PDF", onClick: () => act.viewPdf(q) },
    null,
    { label: "Move to Trash", danger: true, onClick: () => act.trash(q) },
  ];
}

function QuotesList({ tab, groups, totalRows, bookedMatches, filtersActive, selectedId, menuId, onToggleMenu, onCloseMenu, checkedIds, onToggleChecked, act }) {
  if (totalRows === 0 && bookedMatches.length === 0) {
    const emptyText = filtersActive
      ? "No quotes match these filters"
      : tab === "open" ? "No open quotes. Create one with New Quote."
      : tab === "drafts" ? "No drafts."
      : tab === "closed" ? "Nothing closed yet. Lost, archived, and won quotes land here."
      : "Trash is empty.";
    return (
      <div className="sq-list" style={S.list}>
        <div className="sq-empty" style={S.noRows}>
          <div style={{ fontSize: 14, color: C.text, marginBottom: 6 }}>{emptyText}</div>
          {tab === "open" && (
            <div style={{ fontSize: 11.5 }}>Once a quote is booked it becomes a job and moves to the Jobs tab.</div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="sq-list" style={S.list}>
      {groups.map(g => (
        <React.Fragment key={g.key}>
          {g.label && <div className="sq-month" style={S.dateHead}>{g.label}</div>}
          {g.rows.map(q => (
            <QuoteRow
              key={q.id}
              quote={q}
              selected={selectedId === q.id}
              menuOpen={menuId === q.id}
              onClick={() => act.select(q)}
              onToggleMenu={() => onToggleMenu(q.id)}
              onCloseMenu={onCloseMenu}
              checked={checkedIds.has(q.id)}
              onToggleChecked={() => onToggleChecked(q.id)}
              act={act}
            />
          ))}
        </React.Fragment>
      ))}
      {bookedMatches.length > 0 && (
        <>
          <div className="sq-month" style={S.dateHead}>Booked — now jobs ({bookedMatches.length})</div>
          {bookedMatches.map(q => (
            <div
              key={q.id}
              role="button"
              tabIndex={0}
              className="sq-row sq-row-booked"
              onClick={() => act.openJob(q)}
              onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act.openJob(q); } }}
              style={{ ...S.qrow, opacity: 0.72 }}
            >
              {/* spacer keeps the grid aligned — booked rows are not bulk-actionable */}
              <div style={S.cb} />
              <div style={{ minWidth: 0 }}>
                <div style={S.qClient}>{q.clientName || "Untitled"}</div>
                <div style={S.qSub}>{q.contactName || "No contact"}{q.summary ? ` · ${q.summary}` : ""}</div>
              </div>
              <div className="sq-right" style={S.qRight}>
                <span style={S.pill}>
                  <span style={{ width: 7, height: 7, borderRadius: 4, background: QSC.booked, display: "inline-block" }} />
                  <span style={{ color: QSC.booked, fontSize: 11, textTransform: "uppercase" }}>Booked</span>
                </span>
                <div style={S.qTotal}>{fmt(Number(q.total) || 0)}</div>
                <div style={S.qMeta}>Open job →</div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function QuoteRow({ quote, selected, menuOpen, onClick, onToggleMenu, onCloseMenu, checked, onToggleChecked, act }) {
  const color = stateColor(quote._state);
  // In Open, the tab IS the status — a row of identical "QUOTED" pills is noise.
  const showPill = quote._state !== "quoted";
  const fu = quote._fu;
  const showFu = fu && fu.tone !== "later" && fu.label;
  return (
    <div
      role="button"
      tabIndex={0}
      className={selected ? "sq-row sq-row-selected" : "sq-row"}
      onClick={onClick}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      style={{ ...S.qrow, ...(selected ? S.qrowSel : null) }}
    >
      {/* Checking marks for bulk actions; clicking the row previews it. Separate gestures. */}
      <div className={checked ? "sq-cb sq-cb-on" : "sq-cb"} style={S.cb} onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Select ${quote.clientName || "quote"}`}
          checked={checked}
          onChange={onToggleChecked}
          style={S.cbInput}
        />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={S.qClient}>{quote.clientName || "Untitled"}</div>
        <div style={S.qSub}>{quote.contactName || "No contact"}{quote.summary ? ` · ${quote.summary}` : ""}</div>
        {/* Holds (Frame D): the equipment pair, then Holding, Conflict on the first
            colliding day (+N more), and Stale after 30 days without an update. */}
        {((quote._equipment || []).length > 0 || quote._holding) && (
          <div className="sq-tags" style={S.tagRow}>
            {(quote._equipment || []).map((name, i) => <span key={`${name}-${i}`} style={S.tagEq}>{name}</span>)}
            {quote._holding && <span className="sq-badge-holding" style={S.badgeHold}>Holding</span>}
            {quote._holding && (quote._conflicts || []).length > 0 && (
              <span className="sq-badge-conflict" style={S.badgeConf}>
                Conflict · {fmtDate(quote._conflicts[0], FMT_DATE_SHORT)}{quote._conflicts.length > 1 ? ` +${quote._conflicts.length - 1}` : ""}
              </span>
            )}
            {quote._stale && <span className="sq-badge-stale" style={S.badgeStale}>Stale</span>}
          </div>
        )}
        {showFu && (
          <div className="sq-fu" style={{
            ...S.fuBadge,
            color: fu.tone === "overdue" ? C.danger : fu.tone === "today" ? C.amber : C.muted,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: 3, display: "inline-block",
              background: fu.tone === "overdue" ? C.danger : fu.tone === "today" ? C.amber : C.border,
            }} />
            {fu.label}
          </div>
        )}
      </div>
      <div className="sq-right" style={S.qRight}>
        {showPill && (
          <span style={S.pill}>
            <span style={{ width: 7, height: 7, borderRadius: 4, background: color, display: "inline-block" }} />
            <span style={{ color, fontSize: 11, textTransform: "uppercase" }}>{STATE_LABEL[quote._state] || quote._state}</span>
          </span>
        )}
        <div style={S.qTotal}>{fmt(Number(quote.total) || 0)}</div>
        <div style={S.qMeta}>{quote._metaLabel}</div>
      </div>
      <div style={S.kebabWrap} onMouseDown={e => e.stopPropagation()}>
        <button
          type="button"
          className="sq-kebab"
          aria-label={`Actions for ${quote.clientName || "quote"}`}
          style={S.kebab}
          onClick={e => { e.stopPropagation(); onToggleMenu(); }}
        >⋯</button>
        {menuOpen && (
          <div className="sq-menu" style={S.menu} onClick={e => e.stopPropagation()}>
            {menuItemsFor(quote, act).map((it, i) => it === null
              ? <div key={`d${i}`} style={S.menuDivider} />
              : (
                <button
                  key={it.label}
                  type="button"
                  style={{ ...S.menuItem, ...(it.danger ? { color: C.danger } : null) }}
                  onClick={() => { onCloseMenu(); it.onClick(); }}
                >{it.label}</button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* Panel-header ⋯ menu (approved mockup slice1-saved-quotes-panel variant C,
   2026-08-24). Same look and outside-click behavior as the row kebab; holds only
   status changes and destructive actions so everyday buttons stay one click. */
function PanelMenu({ items, up }) {
  const [open, setOpen] = useState(false);
  // Contains-check, not stopPropagation — swallowing mousedown would keep other
  // open menus (row kebabs) from seeing the click and closing themselves.
  // Capture phase: row kebab wrappers stop mousedown propagation, which would
  // starve a bubble-phase listener and leave this menu open alongside a row menu.
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span ref={menuRef} style={S.kebabWrap}>
      <button
        type="button"
        className="sq-kebab"
        aria-label="More actions"
        aria-expanded={open}
        style={S.panelKebab}
        onClick={() => setOpen(o => !o)}
      >⋯</button>
      {open && (
        // In the dock the menu opens upward — downward it would be clipped by the
        // panel's own scroll box.
        <div className="sq-menu" style={{ ...S.menu, ...(up ? S.menuUp : null) }}>
          {items.map((it, i) => it === null
            ? <div key={`d${i}`} style={S.menuDivider} />
            : (
              <button
                key={it.label}
                type="button"
                style={{ ...S.menuItem, ...(it.danger ? { color: C.danger } : null) }}
                onClick={() => { setOpen(false); it.onClick(); }}
              >{it.label}</button>
            ))}
        </div>
      )}
    </span>
  );
}

/* The preview body — shared verbatim by the desktop sticky panel and the phone
   bottom sheet. Keyed by quote id at both call sites so per-quote confirm state
   resets atomically. */
function PanelBody({ quote, previewJob, booking, eraseArmed, locked = "", lockMessage = "", unsavedNote = "", act }) {
  const [confirmBook, setConfirmBook] = useState(false);
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const dockRef = useRef(null);

  // A row's ⋯ menu can arm a destructive confirm without the dock being on
  // screen — the panel is tall and the dock sits at its bottom edge. Never
  // leave a confirm the user can't see.
  useEffect(() => {
    if (!(eraseArmed || confirmBook) || !dockRef.current) return;
    const r = dockRef.current.getBoundingClientRect();
    if (r.bottom > window.innerHeight || r.top < 0) {
      dockRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [eraseArmed, confirmBook]);

  const s = quote._state;
  const kicker =
    s === "quoted" ? "Quote Preview" :
    s === "booked_nojob" ? "Booked — Job Missing" :
    s === "completed" ? "Won Quote" :
    s === "draft" ? "Draft Quote" :
    s === "lost" ? "Lost Quote" :
    s === "archived" ? "Archived Quote" : "In Trash";

  const sessions = quote._sessions || [];
  const equipment = quote._equipment || [];
  const versionsCount = (quote.versions || []).length;
  const bookable = s === "quoted" || s === "booked_nojob";

  const bookButtons = confirmBook ? (
    <span style={S.confirmWrap}>
      <button type="button" style={S.confirmCancel} onClick={() => setConfirmBook(false)}>Cancel</button>
      <button
        type="button"
        disabled={booking || !!locked}
        onClick={() => act.book(quote)}
        style={{ ...btnP, opacity: booking || locked ? 0.65 : 1 }}
      >
        {booking ? "Booking..." : "Confirm — book job"}
      </button>
    </span>
  ) : (
    <button type="button" disabled={booking || !!locked} onClick={() => setConfirmBook(true)} style={{ ...btnP, opacity: booking || locked ? 0.65 : 1 }}>
      Book Job
    </button>
  );

  // Everyday actions as one attached cluster (approved variant C) — shared by
  // the quoted / won / lost / archived headers.
  const actionCluster = (
    <span className="sq-seg" style={S.segGroup}>
      <button type="button" onClick={() => act.edit(quote)} style={S.segBtn}>Edit</button>
      <button type="button" onClick={() => act.viewPdf(quote)} style={{ ...S.segBtn, borderLeftWidth: 1 }}>View PDF</button>
    </span>
  );

  // Everything the dock offers, per state. Focused confirms: while a confirm is
  // armed the dock shows ONLY that confirm, so the choice is unambiguous and the
  // row can never wrap on a 320px sheet.
  const eraseConfirming = eraseArmed && (s === "trashed" || s === "draft");
  const dockRow = confirmBook && bookable ? (
    <div style={S.dockRow}>
      <span style={{ flex: 1 }} />
      {bookButtons}
    </div>
  ) : eraseConfirming ? null : (
    <div style={S.dockRow}>
      {s === "quoted" && (
        <PanelMenu up items={[
          { label: "Duplicate", onClick: () => act.duplicate(quote) },
          null,
          { label: "Mark Lost", onClick: () => act.markLost(quote) },
          { label: "Archive", onClick: () => act.archive(quote) },
          null,
          { label: "Move to Trash", danger: true, onClick: () => act.trash(quote) },
        ]} />
      )}
      {(s === "quoted" || s === "lost" || s === "archived" || s === "completed") && actionCluster}
      {s === "booked_nojob" && (
        <>
          <button type="button" onClick={() => act.edit(quote)} style={btnS}>Edit</button>
          <button type="button" onClick={() => act.revertToOpen(quote)} style={btnS}>Revert to Open</button>
        </>
      )}
      {s === "draft" && (
        <button type="button" onClick={() => act.eraseAsk(quote)} style={{ ...btnS, color: C.danger, borderColor: `${C.danger}40` }}>Delete draft</button>
      )}
      {s === "trashed" && (
        <button type="button" onClick={() => act.eraseAsk(quote)} style={{ ...btnS, color: C.danger, borderColor: `${C.danger}40` }}>Delete forever</button>
      )}
      <span style={{ flex: 1 }} />
      {(s === "quoted" || s === "booked_nojob") && bookButtons}
      {s === "draft" && <button type="button" onClick={() => act.edit(quote)} style={btnP}>Continue editing →</button>}
      {(s === "lost" || s === "archived") && <button type="button" onClick={() => act.restoreToOpen(quote)} style={btnP}>Restore to Open</button>}
      {s === "trashed" && <button type="button" onClick={() => act.restoreFromTrash(quote)} style={btnP}>Restore</button>}
    </div>
  );

  return (
    <div>
      {/* Header carries identity only — every action lives in the dock below. */}
      <div style={S.previewHeader}>
        {s === "quoted" ? (
          <div style={S.kickerRow}>
            <span style={{ ...S.kicker, marginBottom: 0 }}>{kicker}</span>
            <span style={S.pill}>
              <span style={{ width: 7, height: 7, borderRadius: 4, background: QSC.quoted, display: "inline-block" }} />
              <span style={{ color: QSC.quoted, fontSize: 11, textTransform: "uppercase" }}>Quoted</span>
            </span>
          </div>
        ) : (
          <div style={S.kicker}>{kicker}</div>
        )}
        <div style={S.previewTitle}>{quote.clientName || "Untitled quote"}</div>
        <div style={S.previewMeta}>
          {quote.contactName || "No contact"} · {quote.id}
          {(quote.changeOrderOfJobId || quote.changeOrderOfQuoteId) ? " · change order" : ""}
        </div>
      </div>

      {locked && (
        <div data-testid="sq-locked" role="status" style={S.warning}>
          {lockMessage || `${locked} this quote. Its buttons work again when that finishes.`}
        </div>
      )}
      {unsavedNote && (
        <div data-testid="sq-unsaved" style={S.warning}>
          {unsavedNote} Edit shows them. Booking and View PDF use the saved quote without them.
        </div>
      )}
      {s === "booked_nojob" && (
        <div style={S.warning}>
          Marked <strong style={{ fontWeight: 500 }}>booked</strong>, but its linked job no longer exists. Book it again to recreate the job, or revert it to Open. Not counted in the pipeline number.
        </div>
      )}
      {(s === "lost" || s === "archived") && (
        <div style={S.warning}>
          {STATE_LABEL[s]}. Restoring moves it back to Open and into the pipeline number.
        </div>
      )}
      {s === "completed" && (
        <div style={S.wonNote}>
          Won — the job was delivered. This quote is kept as the record of how it ended.
        </div>
      )}
      {s === "trashed" && (
        <div style={S.trashNote}>
          {quote._metaLabel}{quote.jobStatus && quote.jobStatus !== "quoted" ? ` — was ${STATE_LABEL[String(quote.jobStatus).toLowerCase()] || quote.jobStatus}` : " — was Quoted"}. Restore returns it to {["lost", "archived", "completed"].includes(String(quote.jobStatus || "").toLowerCase()) ? "Closed" : "Open"}.
        </div>
      )}

      {s === "quoted" && (
        <FollowUpBox quote={quote} act={act} customDateOpen={customDateOpen} setCustomDateOpen={setCustomDateOpen} />
      )}

      {previewJob && (
        <div style={S.previewGrid}>
          <PreviewField label="Job ID" value={previewJob.jobId || "Pending date"} mono />
          <PreviewField label="Capture Date" value={previewJob.captureDate ? fmtDate(previewJob.captureDate, FMT_DATE_FULL) : "TBD"} />
          <PreviewField label="Location" value={previewJob.location || "TBD"} />
          <PreviewField label="Revenue" value={fmt(previewJob.grossRevenue || 0)} mono />
        </div>
      )}

      <div style={S.section}>
        <div style={S.sectionTitle}>Summary</div>
        <div style={S.bodyText}>{quote.summary || "No summary saved on this quote."}</div>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Equipment</div>
        {equipment.length ? (
          <div style={S.chips}>
            {equipment.map((name, i) => <span key={`${name}-${i}`} style={S.chip}>{name}</span>)}
          </div>
        ) : (
          <div style={S.bodyText}>No equipment selected.</div>
        )}
      </div>

      {quote._holding && (
        <div style={S.section}>
          <div style={S.sectionTitle}>Hold</div>
          <div style={S.bodyText}>
            Holding {describeHoldDates(quote._holdDates || [])} on the calendar while this quote is open.
            {quote._stale ? " No update in 30 days." : ""}
          </div>
          {(quote._conflictLines || []).length > 0 ? (
            <div style={S.holdLines}>
              {quote._conflictLines.map(line => <div key={line} style={S.holdLine}>{line}</div>)}
            </div>
          ) : (
            <div style={{ ...S.bodyText, color: C.muted, marginTop: 4 }}>No conflict on these dates.</div>
          )}
        </div>
      )}

      <div style={S.section}>
        <div style={S.sectionTitle}>Sessions</div>
        {sessions.length ? (
          <div style={S.sessionList}>
            {sessions.map((sess, i) => (
              <div key={sess.id || i} style={S.sessionRow}>
                <div>
                  <div style={{ fontSize: 12, color: C.text }}>
                    {SESSION_TYPE_LABELS[sess.sessionType] || sess.sessionType || "Session"}
                  </div>
                  <div style={S.subline}>{sess.locationVenue || sess.location || previewJob?.location || "Location TBD"}</div>
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, whiteSpace: "nowrap" }}>
                  {sess.date ? fmtDate(sess.date, FMT_DATE_FULL) : "TBD"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={S.bodyText}>Single-day quote. Booking will use the capture date above.</div>
        )}
      </div>

      <div style={S.panelFoot}>
        {versionsCount > 0 && (
          <a style={S.statusLink} onClick={() => act.versions(quote)}>Version history ({versionsCount})</a>
        )}
        {quote.quoteDate && <span>Created {fmtDate(quote.quoteDate, FMT_DATE_SHORT)}</span>}
      </div>

      {/* Confirms live INSIDE the sticky dock: the explanation and the button
          that acts on it stay together and on screen, however far the panel is
          scrolled. */}
      <div className="sq-dock" ref={dockRef}>
        {confirmBook && bookable && !eraseConfirming && (
          <div style={{ ...S.confirmNote, marginBottom: 10 }} data-testid="sq-book-confirm">
            Booking creates a job from this quote and moves it to the <strong style={{ fontWeight: 500 }}>Jobs</strong> tab. Confirm to continue.
            {unsavedNote && <span data-testid="sq-book-unsaved"> {unsavedNote} Booking uses the saved quote without them. To book with them, open Edit and save first.</span>}
          </div>
        )}
        {eraseConfirming && (
          <div style={{ ...S.eraseNote, marginBottom: 0 }}>
            <span>{s === "draft" ? "Delete this draft permanently?" : "Delete this quote forever? This cannot be undone."}</span>
            <span style={S.confirmWrap}>
              <button type="button" style={S.confirmCancel} onClick={act.eraseCancel}>Cancel</button>
              <button type="button" style={S.eraseBtn} onClick={() => act.eraseConfirm(quote)}>
                {s === "draft" ? "Delete draft" : "Delete forever"}
              </button>
            </span>
          </div>
        )}
        {dockRow}
      </div>
    </div>
  );
}

/* One line, not a block: status text, then the only two things you ever do with
   it. The snooze options moved into a menu with plain-English labels — "+5bd"
   asked the reader to decode it every time. */
function FollowUpBox({ quote, act, customDateOpen, setCustomDateOpen }) {
  const fu = quote._fu;
  const tone = fu?.tone;
  const boxStyle = {
    ...S.fuLine,
    ...(tone === "overdue" ? S.fuBoxOverdue : tone === "today" ? S.fuBoxToday : null),
  };
  const dotColor = tone === "overdue" ? C.danger : tone === "today" ? C.amber : C.border;
  return (
    <div className="sq-fubox" style={boxStyle}>
      <span style={{ width: 7, height: 7, borderRadius: 4, background: dotColor, display: "inline-block", flex: "none" }} />
      <span style={S.fuText}>
        {fu ? `Follow-up ${fu.due}` : "No follow-up scheduled"}
      </span>
      <span style={{ flex: 1 }} />
      {quote.followUpDate && (
        <button type="button" style={{ ...S.fuChip, color: C.green, borderColor: `${C.green}50` }} onClick={() => act.followedUp(quote)}>✓ Followed up</button>
      )}
      {customDateOpen ? (
        <input
          type="date"
          autoFocus
          style={S.fuDateInput}
          onBlur={() => setCustomDateOpen(false)}
          onChange={e => { if (e.target.value) { act.snoozeTo(quote, e.target.value); setCustomDateOpen(false); } }}
        />
      ) : (
        <SnoozeMenu
          label={quote.followUpDate ? "Snooze" : "Schedule"}
          items={[
            { label: "In 2 business days", onClick: () => act.snooze(quote, 2) },
            { label: "In 5 business days", onClick: () => act.snooze(quote, 5) },
            { label: "In 10 business days", onClick: () => act.snooze(quote, 10) },
            null,
            { label: "Pick a date…", onClick: () => setCustomDateOpen(true) },
          ]}
        />
      )}
    </div>
  );
}

/* Same outside-click contract as PanelMenu (capture phase — row kebabs stop
   mousedown propagation), styled as a follow-up chip rather than a kebab. */
function SnoozeMenu({ label, items }) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const menuRef = useRef(null);

  // The panel is its own scroll box, so a downward menu can be clipped by it.
  // Measure the room below the trigger on open and flip up when it won't fit.
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const scroller = menuRef.current.closest(".sq-preview, .sq-sheet");
    if (!scroller) return;
    const trigger = menuRef.current.getBoundingClientRect();
    const box = scroller.getBoundingClientRect();
    const MENU_H = 170; // 4 items + divider + padding
    setDropUp(trigger.bottom + MENU_H > box.bottom && trigger.top - MENU_H > box.top);
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span ref={menuRef} style={{ position: "relative", display: "inline-flex", flex: "none" }}>
      <button type="button" style={S.fuChip} aria-expanded={open} onClick={() => setOpen(o => !o)}>{label} ▾</button>
      {/* Vertical flip only — S.menuUp also swaps to left-alignment, which would
          push this right-edge menu off the panel. */}
      {open && (
        <div className="sq-menu" style={{ ...S.menu, minWidth: 190, ...(dropUp ? { top: "auto", bottom: "calc(100% + 4px)" } : null) }}>
          {items.map((it, i) => it === null
            ? <div key={`d${i}`} style={S.menuDivider} />
            : (
              <button
                key={it.label}
                type="button"
                style={S.menuItem}
                onClick={() => { setOpen(false); it.onClick(); }}
              >{it.label}</button>
            ))}
        </div>
      )}
    </span>
  );
}

function PreviewField({ label, value, mono: useMono }) {
  return (
    <div style={S.previewField}>
      <div style={S.fieldLabel}>{label}</div>
      <div style={{ ...S.fieldValue, fontFamily: useMono ? mono : font }}>{value}</div>
    </div>
  );
}

const S = {
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 500,
    letterSpacing: "-0.01em",
    color: C.text,
  },
  pageSub: {
    fontSize: 12,
    color: C.muted,
    marginTop: 2,
  },
  controlRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  viewToggle: {
    display: "inline-flex",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: 4,
    gap: 4,
    maxWidth: "100%",
  },
  // Compact in the command strip: the label and number read as one line, and
  // the long explanation lives in the title tooltip.
  pipeline: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 16px",
  },
  pipelineLabel: {
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.muted,
  },
  pipelineValue: {
    fontSize: 17,
    fontWeight: 500,
    color: C.text,
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1,
  },
  filterRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 12,
    flexWrap: "wrap",
  },
  chipsRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  chipF: {
    padding: "4px 12px",
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: C.surface,
    fontSize: 11,
    color: C.muted,
    cursor: "pointer",
    fontFamily: font,
  },
  chipFActive: {
    background: C.surfaceAlt,
    color: C.text,
    borderColor: "#C9C5BE",
  },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    background: C.text,
    color: "#FFF",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    marginBottom: 10,
  },
  bulkBtn: {
    background: "rgba(255,255,255,0.12)",
    color: "#FFF",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: 5,
    padding: "4px 12px",
    fontSize: 11,
    fontFamily: font,
    cursor: "pointer",
  },
  bulkBtnDanger: {
    borderColor: "rgba(239,68,68,0.65)",
    color: "#FCA5A5",
  },
  bulkLink: {
    background: "transparent",
    border: "none",
    color: "rgba(255,255,255,0.75)",
    textDecoration: "underline",
    fontSize: 11,
    fontFamily: font,
    cursor: "pointer",
    padding: "4px 6px",
  },
  bulkConfirmWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    fontSize: 11.5,
  },
  select: {
    padding: "8px 12px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontFamily: font,
    fontSize: 12.5,
    color: C.text,
    cursor: "pointer",
    minWidth: 150,
    outline: "none",
  },
  search: {
    width: "100%",
    padding: "8px 12px 8px 28px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontFamily: font,
    fontSize: 12.5,
    color: C.text,
    outline: "none",
    boxSizing: "border-box",
  },
  clearBtn: {
    fontSize: 11,
    color: C.muted,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "4px 8px",
    textDecoration: "underline",
    fontFamily: font,
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
    gap: 16,
    alignItems: "start",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    overflow: "visible",
  },
  dateHead: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.muted,
    background: C.bg,
    padding: "7px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  qrow: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
    gap: "6px 8px",
    padding: "13px 8px 13px 8px",
    borderBottom: `1px solid ${C.border}`,
    cursor: "pointer",
    alignItems: "start",
  },
  cb: {
    width: 26,
    display: "flex",
    justifyContent: "center",
    alignSelf: "center",
  },
  cbInput: {
    width: 14,
    height: 14,
    accentColor: C.text,
    cursor: "pointer",
  },
  qrowSel: {
    background: C.surfaceAlt,
    boxShadow: `inset 3px 0 0 ${C.text}`,
  },
  qClient: {
    fontSize: 13.5,
    fontWeight: 500,
    color: C.text,
  },
  // One line, ellipsised: a full summary paragraph per row turned the list into
  // a wall of text (the panel shows the whole thing).
  qSub: {
    fontSize: 11.5,
    color: C.muted,
    marginTop: 2,
    lineHeight: 1.35,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fuBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 10.5,
    marginTop: 4,
  },
  qRight: {
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  qTotal: {
    fontFamily: mono,
    fontSize: 13,
    color: C.text,
    marginTop: 4,
  },
  qMeta: {
    fontSize: 11,
    color: C.muted,
    marginTop: 3,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 10.5,
    textTransform: "uppercase",
  },
  kebabWrap: {
    position: "relative",
    alignSelf: "center",
  },
  kebab: {
    width: 28,
    height: 28,
    border: "none",
    background: "transparent",
    borderRadius: 6,
    color: C.muted,
    fontSize: 15,
    cursor: "pointer",
    lineHeight: 1,
    fontFamily: font,
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 2px)",
    zIndex: 30,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(26,26,26,0.12)",
    minWidth: 168,
    padding: 5,
  },
  menuItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "7px 11px",
    border: "none",
    background: "transparent",
    fontFamily: font,
    fontSize: 12,
    color: C.text,
    borderRadius: 5,
    cursor: "pointer",
  },
  menuUp: {
    top: "auto",
    bottom: "calc(100% + 4px)",
    left: 0,
    right: "auto",
  },
  menuDivider: {
    borderTop: `1px solid ${C.border}`,
    margin: "5px 4px",
  },
  subline: {
    fontSize: 11,
    color: C.muted,
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  noRows: {
    padding: "48px 24px",
    textAlign: "center",
    color: C.muted,
  },
  preview: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: 18,
    minHeight: 280,
    position: "sticky",
    // 64, not 16: the topbar is sticky and 48 tall, so a 16px offset slid the
    // panel's top content underneath it on scroll.
    top: 64,
    maxHeight: "calc(100vh - 80px)",
    overflowY: "auto",
  },
  previewHeader: {
    marginBottom: 14,
  },
  dockRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  confirmWrap: {
    display: "inline-flex",
    gap: 8,
    alignItems: "center",
  },
  confirmCancel: {
    fontSize: 12,
    color: C.muted,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "4px 6px",
    textDecoration: "underline",
    fontFamily: font,
  },
  confirmNote: {
    background: `${C.amber}14`,
    border: `1px solid ${C.amber}40`,
    borderRadius: 6,
    padding: "8px 10px",
    color: C.text,
    fontSize: 11.5,
    marginBottom: 12,
  },
  eraseNote: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    background: "rgba(239,68,68,0.06)",
    border: "1px solid rgba(239,68,68,0.3)",
    borderRadius: 6,
    padding: "8px 10px",
    color: C.text,
    fontSize: 11.5,
    marginBottom: 12,
  },
  eraseBtn: {
    ...btnP,
    background: C.danger,
  },
  kickerRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 5,
  },
  segGroup: {
    display: "inline-flex",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    overflow: "hidden",
  },
  // Longhands only: the divider button adds borderLeftWidth, and mixing that
  // with a `border` shorthand makes React warn on every re-render.
  segBtn: {
    padding: "6px 12px",
    background: "transparent",
    borderWidth: 0,
    borderStyle: "solid",
    borderColor: C.border,
    color: C.muted,
    fontSize: 11.5,
    fontWeight: 400,
    fontFamily: font,
    cursor: "pointer",
  },
  panelKebab: {
    width: 28,
    height: 28,
    border: `1px solid ${C.border}`,
    background: "transparent",
    borderRadius: 6,
    color: C.muted,
    fontSize: 15,
    cursor: "pointer",
    lineHeight: 1,
    fontFamily: font,
  },
  statusLink: {
    color: C.muted,
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: 11,
  },
  fuLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: "8px 8px 8px 12px",
    marginBottom: 12,
    fontSize: 11.5,
    flexWrap: "wrap",
  },
  fuText: {
    color: C.text,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fuBoxOverdue: {
    borderColor: "rgba(239,68,68,0.4)",
    background: "rgba(239,68,68,0.04)",
  },
  fuBoxToday: {
    borderColor: `${C.amber}70`,
    background: `${C.amber}0D`,
  },
  fuChip: {
    padding: "4px 10px",
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: C.surface,
    fontSize: 10.5,
    color: C.muted,
    cursor: "pointer",
    fontFamily: font,
  },
  fuDateInput: {
    padding: "3px 8px",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontFamily: font,
    fontSize: 11,
    color: C.text,
    background: C.surface,
    outline: "none",
  },
  kicker: {
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.muted,
    marginBottom: 5,
  },
  previewTitle: {
    fontSize: 17,
    fontWeight: 500,
    color: C.text,
  },
  previewMeta: {
    fontSize: 11,
    color: C.muted,
    marginTop: 3,
    fontFamily: mono,
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    marginBottom: 14,
  },
  previewField: {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: "10px 11px",
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.muted,
    marginBottom: 4,
  },
  fieldValue: {
    fontSize: 12.5,
    color: C.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  section: {
    borderTop: `1px solid ${C.border}`,
    paddingTop: 13,
    marginTop: 13,
  },
  sectionTitle: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.muted,
    marginBottom: 8,
  },
  bodyText: {
    fontSize: 12.5,
    lineHeight: 1.5,
    color: C.text,
  },
  chips: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 9px",
    borderRadius: 5,
    background: C.bg,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 11,
  },
  // Holds (Frame D): the tag line under a row's subtitle, and the panel's lines.
  tagRow: {
    display: "flex",
    gap: 5,
    marginTop: 6,
    flexWrap: "wrap",
    alignItems: "center",
  },
  tagEq: {
    fontSize: 10,
    padding: "2px 7px",
    borderRadius: 4,
    background: `${C.blue}14`,
    color: C.blue,
  },
  badgeHold: {
    fontSize: 9,
    padding: "2px 7px",
    borderRadius: 4,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 500,
    border: `1px dashed ${C.blue}a0`,
    color: C.blue,
  },
  badgeConf: {
    fontSize: 9,
    padding: "2px 7px",
    borderRadius: 4,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 500,
    background: `${C.amber}1f`,
    color: C.capture,
  },
  badgeStale: {
    fontSize: 9,
    padding: "2px 7px",
    borderRadius: 4,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 500,
    background: C.surfaceAlt,
    color: C.muted,
  },
  holdLines: {
    display: "grid",
    gap: 6,
    marginTop: 8,
  },
  holdLine: {
    fontSize: 12,
    lineHeight: 1.45,
    color: C.capture,
    background: `${C.amber}14`,
    border: `1px solid ${C.amber}40`,
    borderRadius: 6,
    padding: "7px 10px",
  },
  sessionList: {
    display: "grid",
    gap: 6,
  },
  sessionRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    padding: "8px 0",
    borderBottom: `1px solid ${C.border}`,
  },
  panelFoot: {
    borderTop: `1px solid ${C.border}`,
    marginTop: 13,
    paddingTop: 11,
    fontSize: 11,
    color: C.muted,
    display: "flex",
    gap: 14,
    alignItems: "center",
  },
  warning: {
    background: `${C.amber}14`,
    border: `1px solid ${C.amber}40`,
    borderRadius: 6,
    padding: "8px 10px",
    color: C.text,
    fontSize: 11.5,
    marginBottom: 12,
  },
  wonNote: {
    background: "rgba(16,185,129,0.07)",
    border: "1px solid rgba(16,185,129,0.3)",
    borderRadius: 6,
    padding: "8px 10px",
    color: C.text,
    fontSize: 11.5,
    marginBottom: 12,
  },
  trashNote: {
    background: "rgba(239,68,68,0.05)",
    border: "1px solid rgba(239,68,68,0.25)",
    borderRadius: 6,
    padding: "8px 10px",
    color: C.text,
    fontSize: 11.5,
    marginBottom: 12,
  },
  error: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
    borderRadius: 6,
    padding: "9px 12px",
    color: C.danger,
    fontSize: 12,
    marginBottom: 12,
  },
  empty: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: 32,
    textAlign: "center",
    color: C.muted,
    fontSize: 13,
    fontWeight: 300,
    fontFamily: font,
  },
  previewEmpty: {
    height: 220,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: C.muted,
    fontSize: 12,
  },
};
